import { useTranslation } from 'react-i18next'

import { useCampaignDeliverabilityApiV1CampaignsCampaignIdDeliverabilityGet } from '#/shared/api/generated/communication/theCargoCommunication'
import type {
  CampaignDeliverabilityKind,
  CampaignDeliverabilityRow,
} from '#/shared/api/generated/communication/models'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '#/shared/ui/dialog'
import { Skeleton } from '#/shared/ui/skeleton'

interface CampaignDeliverabilityDialogProps {
  campaignId: string
  /** Which drill-down is open, or null when none is. */
  kind: CampaignDeliverabilityKind | null
  onClose: () => void
}

function RowSkeleton() {
  return (
    <li className="flex flex-col gap-1.5 border-b border-gray-200 px-5 py-3 last:border-b-0">
      <Skeleton className="h-4 w-48" />
      <Skeleton className="h-3.5 w-72 max-w-full" />
    </li>
  )
}

function DeliverabilityRow({ row }: { row: CampaignDeliverabilityRow }) {
  return (
    <li className="flex flex-col gap-1 border-b border-gray-200 px-5 py-3 last:border-b-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="text-sm font-medium text-gray-900">{row.name}</span>
        <span className="text-xs text-gray-300">{row.when}</span>
      </div>
      <span className="text-xs text-gray-500">{row.contact}</span>
      <span className="text-xs text-campaign-stopped">
        {row.detail == null ? row.reason : `${row.detail} · ${row.reason}`}
      </span>
      <span className="text-xs text-gray-300">{row.step}</span>
    </li>
  )
}

function DeliverabilityList({
  rows,
  isPending,
}: {
  rows: CampaignDeliverabilityRow[]
  isPending: boolean
}) {
  const { t } = useTranslation()
  return (
    <ul className="min-h-0 flex-1 overflow-y-auto">
      {isPending && [0, 1, 2, 3].map((index) => <RowSkeleton key={index} />)}
      {!isPending && rows.length === 0 && (
        <li className="px-5 py-10 text-center text-xs text-gray-300">
          {t('campaigns.detail.deliverability.empty')}
        </li>
      )}
      {rows.map((row) => (
        <DeliverabilityRow key={row.id} row={row} />
      ))}
    </ul>
  )
}

/**
 * Who a step could not reach, and why.
 *
 * A bounce's `detail` separates a hard bounce (suppress the address) from a
 * soft one (retry later); it is null for anything that bounced before the
 * service started recording the provider's type, and an unknown type must not
 * be drawn as "hard" — so the line is simply absent.
 */
export function CampaignDeliverabilityDialog({
  campaignId,
  kind,
  onClose,
}: CampaignDeliverabilityDialogProps) {
  const { t } = useTranslation()
  const query =
    useCampaignDeliverabilityApiV1CampaignsCampaignIdDeliverabilityGet(
      campaignId,
      { kind: kind ?? undefined },
      { query: { enabled: kind != null } },
    )
  const data = query.data?.data
  const rows = data?.rows ?? []

  return (
    <Dialog open={kind != null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[560px]">
        <header className="flex shrink-0 flex-col gap-1 border-b border-gray-200 px-5 py-4">
          <DialogTitle className="text-base font-semibold text-gray-900">
            {t(`campaigns.detail.deliverability.title.${kind ?? 'failed'}`)}
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-500">
            {t('campaigns.detail.deliverability.count', {
              count: data?.total ?? 0,
            })}
          </DialogDescription>
        </header>
        <DeliverabilityList rows={rows} isPending={query.isPending} />
      </DialogContent>
    </Dialog>
  )
}
